import { AfterViewInit, Component, computed, effect, ElementRef, NgZone, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { AuthService, CustomersService, GlobalsService, JobsService, LoginService, SleepService } from "@services";
import { CorporateStore, TasksStore, TimeZoneStore } from "@stores";
import { DropdownModel, ICorporateCustomerSearchListModel, ICorporateJobSearchListModel, IDropdownModel, IJobModel } from "@models";
import { VersionReleaseDialogComponent } from "@shared-components/version-release-dialog";

type HeaderSearchResult =
    (ICorporateCustomerSearchListModel & { resultType: 'customer'; displayLabel: string }) |
    (ICorporateJobSearchListModel & { resultType: 'job'; displayLabel: string });

@Component({
    selector: "header-bar",
    templateUrl: "header-bar.component.html",
    styleUrls: ['header-bar.component.css'],
    providers: [JobsService],
    standalone: false
})

export class HeaderBarComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('versionDialog') versionDialog: VersionReleaseDialogComponent; // Reference to the dialog component

    @ViewChild('popoverRef', { static: true }) popoverRef: ElementRef<HTMLElement>;

    @ViewChild('headerLeft', { static: true }) headerLeftRef: ElementRef<HTMLElement>;
    @ViewChild('headerRight', { static: true }) headerRightRef: ElementRef<HTMLElement>;

    private headerSizeObserver: ResizeObserver;

    firstName: string;
    profilePictureUrl: string;
    readonly isCorporate = signal<boolean>(GlobalsService.isCorporate);
    readonly isDispatch = signal<boolean>(GlobalsService.userInfo.isDispatch);
    readonly franchises = computed(() => this.corporateStore.franchises()?.map(x => new DropdownModel(x.companyId, `${x.companyName} - ${x.pendingJobCount}`)));
    readonly isTestCompany = signal<boolean>(GlobalsService.isDebug || GlobalsService.companySettings.isTestCompany);
    readonly selectedFranchiseId = computed(() => this.corporateStore.selectedFranchise()?.companyId);
    readonly timeZones = computed(() => this.timeZoneStore.timeZones());
    readonly selectedTimeZoneId = computed(() => this.timeZoneStore.selectedTimeZone());
    canViewTasks: boolean = GlobalsService.checkPermission("Tasks", "View");
    isBlockedFromFeatureIdeas: boolean = GlobalsService.userInfo.isBlockedFromFeatureIdeas;
    isLearningPathsEnabled: boolean = GlobalsService.userInfo.isLearningPathsEnabled === true;
    jobSightId: string = GlobalsService.companySettings.jobSightId || '';
    showDialog = false;  // To control if the dialog is open or not

    // Dispatch customer + job search
    searchText: string;
    searchResults: HeaderSearchResult[] = [];

    constructor(private loginService: LoginService,
        private authService: AuthService,
        private customersService: CustomersService,
        private jobsService: JobsService,
        private zone: NgZone,
        protected corporateStore: CorporateStore,
        protected tasksStore: TasksStore,
        protected timeZoneStore: TimeZoneStore) {
        this.firstName = GlobalsService.userInfo.firstName;
        this.profilePictureUrl = GlobalsService.userInfo.profilePictureUrl;
    }

    async ngOnInit() {

        // If this is a corporate user then they can click on a job and have it redirect to show that job
        if (GlobalsService.userInfo.isDispatch || GlobalsService.userInfo.isCorporateAdmin) {
            this.tasksStore.loadJob$.subscribe(job => {
                if (this.selectedFranchiseId() != job.companyId) {
                    this.corporateStore.selectFranchise(job.companyId);
                    this.timeZoneStore.setTimeZone(this.corporateStore.franchises()?.find(x => x.companyId === job.companyId).timeZone);
                }
            });
        }
    }

    ngAfterViewInit() {
        this.publishHeaderWidths();
    }

    ngOnDestroy() {
        this.headerSizeObserver?.disconnect();
        // Nothing is rendering into this row any more (iframe views, logout), so let the page bars go full width
        // rather than reserving space for controls that are gone.
        document.documentElement.style.removeProperty('--header-left-width');
        document.documentElement.style.removeProperty('--header-right-width');
    }

    // Each page renders its own .header-bar and drops it, absolutely positioned, into this component's row.
    // It used to reserve a hard-coded 450px for the controls on the right - roughly 300px short of what the
    // search box, Sign Out, tasks, notifications, Need Help and the avatar actually take - so page titles and
    // buttons ran underneath the global search. The corporate franchise/timezone dropdowns on the left had the
    // same collision with nothing reserved at all. Publishing both measured widths keeps the page bar correct
    // for every permission set and window size; the vars are simply absent when there's no header (iframe
    // views), which is exactly when a page bar should span the full width.
    private publishHeaderWidths() {
        const left = this.headerLeftRef?.nativeElement;
        const right = this.headerRightRef?.nativeElement;
        if (!left || !right) return;

        const write = () => {
            const style = document.documentElement.style;
            style.setProperty('--header-left-width', `${Math.ceil(left.getBoundingClientRect().width)}px`);
            style.setProperty('--header-right-width', `${Math.ceil(right.getBoundingClientRect().width)}px`);
        };

        // Outside the zone: these fire on every window resize and only ever write CSS variables.
        this.zone.runOutsideAngular(() => {
            this.headerSizeObserver = new ResizeObserver(write);
            this.headerSizeObserver.observe(left);
            this.headerSizeObserver.observe(right);
        });

        write();
    }

    onFranchiseChange(franchise: IDropdownModel) {
        this.corporateStore.selectFranchise(franchise.id);
        this.timeZoneStore.setTimeZone(this.corporateStore.franchises()?.find(x => x.companyId === franchise.id).timeZone);
    }

    onTimeZoneChange(timeZone: IDropdownModel) {
        this.timeZoneStore.setTimeZone(timeZone.id);
    }

    async onHeaderSearch(searchText: string) {
        const [customers, jobs] = await Promise.all([
            this.customersService.searchCustomersAcrossBranches(searchText),
            this.jobsService.searchJobsAcrossBranches(searchText)
        ]);

        const customerResults: HeaderSearchResult[] = (customers ?? []).map(c => ({
            ...c,
            resultType: 'customer' as const,
            displayLabel: c.companyName || c.fullName
        }));

        const jobResults: HeaderSearchResult[] = (jobs ?? []).map(j => ({
            ...j,
            resultType: 'job' as const,
            displayLabel: j.jobName
        }));

        this.searchResults = [...customerResults, ...jobResults];
    }

    async onSearchResultSelect(result: HeaderSearchResult) {
        if (!result) return;
        this.searchText = '';

        if (result.resultType === 'job') {
            // Don't fetch the job over HTTP — the JWT is still scoped to the current
            // franchise so the call would hit the wrong branch. The loadJob$ subscribers
            // only need uuid + companyId to switch franchise and navigate the iframe.
            this.tasksStore.loadJob$.next(<IJobModel>{
                uuid: result.jobUuid,
                companyId: result.branchCompanyId
            });
        }
        else {
            // Customer: just switch to the owning branch (preserves prior behavior)
            if (this.selectedFranchiseId() !== result.branchCompanyId) {
                this.corporateStore.selectFranchise(result.branchCompanyId);
                const franchise = this.corporateStore.franchises()?.find(x => x.companyId === result.branchCompanyId);
                if (franchise) {
                    this.timeZoneStore.setTimeZone(franchise.timeZone);
                }
            }
        }
    }

    async logout() {
        await this.loginService.logout("desktop");
        this.authService.clearToken();
        window.location.href = '/';
    }

    idStartsWith911(): boolean {
        return this.jobSightId?.toString().startsWith('911');
    }

    async showVersionHistory() {
        this.versionDialog.showLastFiveVersions();
        await SleepService.sleep(250);
        this.showDialog = true;
    }


    onVersionDialogClose() {
        if (this.versionDialog?.dialogElement) {
            const dialogElement = this.versionDialog.dialogElement.nativeElement;
            const buttonRect = this.popoverRef.nativeElement.getBoundingClientRect();
            const dialogRect = dialogElement.getBoundingClientRect();
            let offset = 10;
            const translateX = buttonRect.left - dialogRect.left + offset; // Adjust as needed
            const translateY = buttonRect.top - dialogRect.top + (buttonRect.height / 2) - (dialogRect.height / 2);

            dialogElement.style.transition = 'transform 0.7s cubic-bezier(0.8, 0, 0.2, 1), opacity 0.7s ease';
            dialogElement.style.transformOrigin = 'left center';

            // Start the fade-out animation along with the translation
            dialogElement.style.animation = 'fadeOut 0.7s forwards'; // Use the fadeOut keyframes
            dialogElement.style.transform = `translate(${translateX}px, ${translateY}px) scale(0.00)`; // Shrink and move the dialog

            setTimeout(() => {
                this.showDialog = false;
                dialogElement.style.transition = 'none';
                dialogElement.style.transform = 'translate(0, 0) scale(1)';
                dialogElement.style.opacity = '1'; // Reset opacity after closing
                dialogElement.style.animation = ''; // Remove the animation

                // Trigger the "pop" effect on the version release button after the dialog closes
                const buttonElement = this.popoverRef.nativeElement;
                buttonElement.style.transition = 'transform 0.3s ease';
                buttonElement.style.transform = 'scale(1.1)'; // Slightly enlarge the button
                setTimeout(() => {
                    buttonElement.style.transform = 'scale(1)'; // Return to normal size
                }, 300);
            }, 700); // Match the timing of the animation
        }
    }

    getReleaseNotesButton(): ElementRef {
        return this.popoverRef;
    }
}